import { createApp } from 'vue';
import { createMyVuetify } from '@/plugins/vuetify';
import { log } from '@/utils/vueLogger.js';
import { iframeDataBridge } from '@/iframe/iframeDataBridge.js';
import { isSyncCompleted, isBackgroundSyncCompleted } from '@/sync/dataSync.js';
import WidgetLayout from '@/components/widgets/WidgetLayout.vue';
import CalendarMap from '@/components/map/CalendarMap.vue';
import POSMap from '@/components/map/POSMap.vue';
import KpiStatementZone from '@/components/kpi/KpiStatementZone.vue';
import PointsStatementZone from '@/components/points/PointsStatementZone.vue';
import PointsDialogZone from '@/components/points/PointsDialogZone.vue';

let vuetifyStylesInjected = false;
const activeIntervals = new Map();
const dynamicApps = new Map();
const zoneComponents = [
	{
		selector: 'vue-widgets-zone',
		component: WidgetLayout,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-calendar-map-zone',
		component: CalendarMap,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-kpi-statement-zone',
		component: KpiStatementZone,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-points-statement-zone',
		component: PointsStatementZone,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-points-dialog-zone',
		component: PointsDialogZone,
		isMounted: false,
		app: null,
		mountedEl: null,
		persistent: true,
	},
];

/**
 * Inject Vuetify's dynamically generated styles into the target iframe document
 */
function injectVuetifyStyles() {
	const targetDocument = iframeDataBridge.getIframeWindow().document;
	const head = targetDocument.head;
	const isLocal = window.location.href.includes('localhost');

	[...targetDocument.querySelectorAll('[data-injected-style]')].forEach((e) => e.remove());

	const allStyleNodes = [...document.styleSheets].map((s) => s.ownerNode).filter(Boolean);
	const styles = isLocal ? allStyleNodes.filter((n) => n.tagName === 'STYLE' && (n.getAttribute('data-vite-dev-id') || n.innerText.includes('.v-'))) : [...document.querySelectorAll('link[rel="stylesheet"]')].filter((link) => link.href.includes('index') || link.href.includes('vuetify'));

	if (!styles.length) {
		log('warn', 'zoneMount', '⚠️ No Vuetify styles found in parent document');
	}

	styles.forEach((style) => {
		const clone = style.cloneNode(true);
		clone.setAttribute('data-injected-style', 'true');
		head.appendChild(clone);
	});

	const themeStyle = document.getElementById('vuetify-theme-stylesheet');

	if (themeStyle && !targetDocument.head.querySelector('#vuetify-theme-stylesheet')) {
		const clone = themeStyle.cloneNode(true);
		clone.setAttribute('data-injected-style', 'true');
		targetDocument.head.appendChild(clone);
		log('info', 'zoneMount', '✅ Injected Vuetify theme styles');
	}

	if (window.location.href.includes('localhost') && !vuetifyStylesInjected) {
		let reinjectTimeout;

		const observer = new MutationObserver(() => {
			clearTimeout(reinjectTimeout);
			reinjectTimeout = setTimeout(() => {
				const iframe = document.querySelector('iframe');
				if (iframe?.contentDocument) {
					log('info', 'zoneMount', '♻️ CSS updated — reinjecting into iframe');
					injectVuetifyStyles(iframe.contentDocument);
				}
			}, 200);
		});

		const styleNodes = [...document.querySelectorAll('style[data-vite-dev-id]')];

		styleNodes.forEach((style) => {
			observer.observe(style, {
				subtree: true,
				childList: true,
			});
		});
	}
}

/**
 * Mounts a Vue component in a target element inside an iframe
 * @param {HTMLElement|null} el - Target element in iframe
 * @param {string} zoneSelector - The id of the element to mount into
 * @returns {boolean} - Whether the mount was successful
 */
export function mountElement(el, zoneSelector) {
	const externis = iframeDataBridge.getExternis(false);

	const isSynchronizing = externis ? Object.values(externis.synchronizing).includes(true) : false;
	let zoneComponent = zoneComponents.find((zc) => zc.selector == zoneSelector);

	// Already mounted
	if (zoneComponent.isMounted) return true;

	if (!isSyncCompleted()) {
		log('warn', 'zoneMount', `Mounting ${zoneSelector} skipped — main sync not yet completed.`);
		return false;
	}

	if (!isBackgroundSyncCompleted()) {
		log('warn', 'zoneMount', `Mounting ${zoneSelector} skipped — background sync not yet completed.`);
		return false;
	}

	if (el && !isSynchronizing) {
		const rawLocale = iframeDataBridge.getCurrentUser()?.language || 'fr-FR';
		const userLocale = rawLocale.split('-')[0];

		const app = createApp(zoneComponent.component);
		const overlayRoot = el.ownerDocument.getElementById('vue-overlay-root');
		app.provide('overlayTarget', overlayRoot);

		app.use(createMyVuetify(userLocale));
		app.mount(el);

		zoneComponent.app = app;
		zoneComponent.isMounted = true;
		zoneComponent.mountedEl = el;

		return true;
	} else {
		if (isSynchronizing) log('warn', 'zoneMount', `Mounting ${zoneSelector} skipped due to synchronizing state.`);

		return false;
	}
}

/**
 * Repeatedly checks for the target zone in the iframe, and mounts the component once available.
 * Also handles remounting if the element is detached from DOM (e.g. by a KO ko:if).
 * Also injects Vuetify styles into the iframe
 * @param {string} zoneSelector - id of the mount target
 */
export function mountZone(zoneSelector) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	const zone = zoneComponents.find((zc) => zc.selector === zoneSelector);
	const MAX_NOT_FOUND = 30;
	let notFoundCount = 0;

	if (!vuetifyStylesInjected) {
		injectVuetifyStyles();
		vuetifyStylesInjected = true;
	}

	if (activeIntervals.has(zoneSelector)) {
		clearInterval(activeIntervals.get(zoneSelector));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneSelector);

		// If mounted but element was removed OR replaced by a new DOM node (e.g. KO rebinding), reset so we can remount
		if (zone.isMounted && (!el || !el.isConnected || zone.mountedEl !== el)) {
			if (zone.app) {
				zone.app.unmount();
				zone.app = null;
			}
			zone.isMounted = false;
			zone.mountedEl = null;
			notFoundCount = 0;
			log('warn', 'zoneMount', `Zone ${zoneSelector} was detached or replaced — will remount when element reappears`);
		}

		if (!zone.isMounted) {
			if (!el) {
				notFoundCount++;
				if (notFoundCount >= MAX_NOT_FOUND) {
					clearInterval(mountInterval);
					activeIntervals.delete(zoneSelector);
					log('warn', 'zoneMount', `Zone ${zoneSelector} not found after ${MAX_NOT_FOUND}s — giving up.`);
					return;
				}
				if (notFoundCount === 1) {
					log('warn', 'zoneMount', `Element with selector ${zoneSelector} not found in iframe document. Retrying...`);
				}
			} else {
				notFoundCount = 0;
				mountElement(el, zoneSelector);
			}
		}
	}, 1000);

	activeIntervals.set(zoneSelector, mountInterval);
}

export function mountDynamicPointsZone(zoneId) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	let notFoundCount = 0;
	const MAX_NOT_FOUND = 15;

	if (!vuetifyStylesInjected) {
		injectVuetifyStyles();
		vuetifyStylesInjected = true;
	}

	if (activeIntervals.has(zoneId)) {
		clearInterval(activeIntervals.get(zoneId));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneId);
		const state = dynamicApps.get(zoneId);

		if (state?.isMounted && (!el || !el.isConnected || state.mountedEl !== el)) {
			state.app.unmount();
			dynamicApps.delete(zoneId);
		}

		if (!dynamicApps.get(zoneId)?.isMounted) {
			if (!el) {
				if (++notFoundCount >= MAX_NOT_FOUND) {
					clearInterval(mountInterval);
					activeIntervals.delete(zoneId);
					log('warn', 'zoneMount', `Dynamic zone ${zoneId} not found after ${MAX_NOT_FOUND}s — giving up.`);
				}
				return;
			}

			if (!isSyncCompleted() || !isBackgroundSyncCompleted()) return;

			const rawLocale = iframeDataBridge.getCurrentUser()?.language || 'fr-FR';
			const app = createApp(PointsStatementZone);
			app.provide('overlayTarget', el.ownerDocument.getElementById('vue-overlay-root'));
			app.use(createMyVuetify(rawLocale.split('-')[0]));
			app.mount(el);

			dynamicApps.set(zoneId, { app, isMounted: true, mountedEl: el });
			clearInterval(mountInterval);
			activeIntervals.delete(zoneId);
			log('info', 'zoneMount', `✅ Dynamic zone mounted: ${zoneId}`);
		}
	}, 500);

	activeIntervals.set(zoneId, mountInterval);
}

export function mountDynamicPosMapZone(zoneId) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	let notFoundCount = 0;
	const MAX_NOT_FOUND = 30;

	if (!vuetifyStylesInjected) {
		injectVuetifyStyles();
		vuetifyStylesInjected = true;
	}

	if (activeIntervals.has(zoneId)) {
		clearInterval(activeIntervals.get(zoneId));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneId);
		const state = dynamicApps.get(zoneId);

		if (state?.isMounted && (!el || !el.isConnected || state.mountedEl !== el)) {
			state.app.unmount();
			dynamicApps.delete(zoneId);
		}

		if (!dynamicApps.get(zoneId)?.isMounted) {
			if (!el) {
				if (++notFoundCount >= MAX_NOT_FOUND) {
					clearInterval(mountInterval);
					activeIntervals.delete(zoneId);
					log('warn', 'zoneMount', `Dynamic map zone ${zoneId} not found after ${MAX_NOT_FOUND}s — giving up.`);
				}
				return;
			}

			if (!isSyncCompleted() || !isBackgroundSyncCompleted()) return;

			const rawLocale = iframeDataBridge.getCurrentUser()?.language || 'fr-FR';
			const app = createApp(POSMap);
			app.provide('overlayTarget', el.ownerDocument.getElementById('vue-overlay-root'));
			app.use(createMyVuetify(rawLocale.split('-')[0]));
			app.mount(el);

			dynamicApps.set(zoneId, { app, isMounted: true, mountedEl: el });
			clearInterval(mountInterval);
			activeIntervals.delete(zoneId);
			log('info', 'zoneMount', `✅ Dynamic map zone mounted: ${zoneId}`);
		}
	}, 500);

	activeIntervals.set(zoneId, mountInterval);
}

export function mountDynamicKpiPosZone(zoneId) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	let notFoundCount = 0;
	const MAX_NOT_FOUND = 30;

	if (!vuetifyStylesInjected) {
		injectVuetifyStyles();
		vuetifyStylesInjected = true;
	}

	if (activeIntervals.has(zoneId)) {
		clearInterval(activeIntervals.get(zoneId));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneId);
		const state = dynamicApps.get(zoneId);

		if (state?.isMounted && (!el || !el.isConnected || state.mountedEl !== el)) {
			state.app.unmount();
			dynamicApps.delete(zoneId);
		}

		if (!dynamicApps.get(zoneId)?.isMounted) {
			if (!el) {
				if (++notFoundCount >= MAX_NOT_FOUND) {
					clearInterval(mountInterval);
					activeIntervals.delete(zoneId);
					log('warn', 'zoneMount', `Dynamic KPI zone ${zoneId} not found after ${MAX_NOT_FOUND}s — giving up.`);
				}
				return;
			}

			if (!isSyncCompleted() || !isBackgroundSyncCompleted()) return;

			const rawLocale = iframeDataBridge.getCurrentUser()?.language || 'fr-FR';
			const app = createApp(KpiStatementZone);
			app.provide('overlayTarget', el.ownerDocument.getElementById('vue-overlay-root'));
			app.use(createMyVuetify(rawLocale.split('-')[0]));
			app.mount(el);

			dynamicApps.set(zoneId, { app, isMounted: true, mountedEl: el });
			clearInterval(mountInterval);
			activeIntervals.delete(zoneId);
			log('info', 'zoneMount', `✅ Dynamic KPI zone mounted: ${zoneId}`);
		}
	}, 500);

	activeIntervals.set(zoneId, mountInterval);
}

export function unmountAll() {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;

	// Unmount dynamic per-campaign zones
	dynamicApps.forEach(({ app }, zoneId) => {
		if (app) {
			app.unmount();
			const el = iframeDocument.getElementById(zoneId);
			if (el) el.innerHTML = '';
			log('info', 'zoneMount', `🔌 Unmounted dynamic zone ${zoneId}`);
		}
	});
	dynamicApps.clear();

	// Clear intervals only for non-persistent zones
	for (const [selector, interval] of activeIntervals) {
		const zone = zoneComponents.find((zc) => zc.selector === selector);
		if (!zone?.persistent) {
			clearInterval(interval);
			activeIntervals.delete(selector);
		}
	}

	zoneComponents.forEach((zone) => {
		if (zone.persistent) return;
		if (zone.isMounted && zone.app) {
			zone.app.unmount();
			zone.app = null;
			zone.isMounted = false;
			zone.mountedEl = null;

			const el = iframeDocument.getElementById(zone.selector);
			if (el) el.innerHTML = '';
			log('info', 'zoneMount', `🔌 Unmounted ${zone.selector}`);
		}
	});

	// Keep styles if a persistent zone is still mounted
	const hasPersistentMounted = zoneComponents.some((z) => z.persistent && z.isMounted);
	if (!hasPersistentMounted) {
		[...iframeDocument.querySelectorAll('[data-injected-style]')].forEach((e) => e.remove());
		vuetifyStylesInjected = false;
	}

	log('success', 'zoneMount', '✅ All zones unmounted');

	// Remove overflow styles
	const contentWrapper = iframeDocument.querySelector('.content-wrapper');
	if (contentWrapper) {
		contentWrapper.style.overflow = '';
		contentWrapper.style.maxHeight = '';
		log('info', 'zoneMount', '✅ Cleared overflow styles from content-wrapper');
	}
}
